import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import { useLocale } from '../../i18n/I18n';
import { contactData } from '../../data/mock';

let scriptPromise;
function loadTurnstile() {
  if (window.turnstile?.render) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    let settled = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (error) { script.remove(); scriptPromise = null; reject(error); }
      else resolve(window.turnstile);
    };
    const timeout = setTimeout(() => finish(new Error('Verification unavailable')), 12000);
    script.onload = () => {
      if (!window.turnstile?.render) finish(new Error('Verification unavailable'));
      else finish();
    };
    script.onerror = () => finish(new Error('Verification unavailable'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export default function ContactVerification({ onToken, resetPass = 0 }) {
  const { t, language } = useLocale();
  const mount = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState('checking');
  const [size, setSize] = useState('flexible');
  useEffect(() => {
    const updateSize = () => {
      const width = mount.current?.getBoundingClientRect().width || 0;
      if (width > 0) setSize(width < 300 ? 'compact' : 'flexible');
    };
    updateSize();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(updateSize) : null;
    if (mount.current) observer?.observe(mount.current);
    window.addEventListener('resize', updateSize);
    return () => { observer?.disconnect(); window.removeEventListener('resize', updateSize); };
  }, []);
  useEffect(() => {
    let cancelled = false;
    let widget;
    let api;
    const controller = new AbortController();
    setPhase('checking');
    onToken('');
    const invalidate = () => { if (!cancelled) { onToken(''); setPhase('failed'); } };
    async function start() {
      try {
        const response = await axios.get('/contact-config.php', { timeout: 8000, signal: controller.signal });
        const { available, siteKey } = response.data || {};
        if (available !== true || typeof siteKey !== 'string' || !/^[a-zA-Z0-9_-]{20,100}$/.test(siteKey)) throw new Error('Verification unavailable');
        if (cancelled) return;
        api = await loadTurnstile();
        if (cancelled) return;
        widget = api.render(mount.current, {
          sitekey: siteKey, action: 'contact', theme: 'light', size,
          language: language === 'zh' ? 'zh-CN' : language,
          'response-field': false,
          callback: token => { if (!cancelled) { onToken(token); setPhase('verified'); } },
          'expired-callback': () => { if (!cancelled) { onToken(''); setPhase('checking'); } },
          'timeout-callback': invalidate,
          'error-callback': () => { invalidate(); return true; },
        });
      } catch (error) { if (!cancelled) invalidate(); }
    }
    start();
    return () => { cancelled = true; controller.abort(); if (widget !== undefined) api?.remove(widget); };
  }, [onToken, language, resetPass, attempt, size]);

  return <div className={`bz-contact-verification is-${phase}`}>
    <p className="bz-contact-verification-title"><ShieldCheck size={17} aria-hidden="true"/>{t('Protección contra spam')}</p>
    <div ref={mount} className="bz-contact-verification-widget"/>
    <p className="bz-contact-verification-status" role="status" aria-live="polite">{t(phase === 'verified' ? 'Verificación completada.' : phase === 'failed' ? 'La verificación no está disponible. Tus datos siguen aquí.' : 'Comprobando la seguridad del formulario…')}</p>
    {phase === 'failed' && <div className="bz-contact-verification-actions">
      <button type="button" onClick={() => setAttempt(value => value + 1)}><RefreshCw size={14} aria-hidden="true"/>{t('Reintentar verificación')}</button>
      <a href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">{t('Contactar por WhatsApp')}</a>
    </div>}
  </div>;
}
